import React, { useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Menu, X } from "lucide-react";
import { LanguageContext } from "../App";
import { languageNames } from "../i18n/translations";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { lang, setLang, t } = useContext(LanguageContext);

  const handleLangChange = (e) => setLang(e.target.value);

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <nav className="bg-slate-950/70 backdrop-blur-xl border-b border-blue-500/10 shadow-2xl shadow-blue-950/30 rounded-b-3xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <img
                src="/logo.jpg"
                alt="AI Workshop LLC"
                className="h-9 w-auto rounded-xl shadow-lg group-hover:shadow-blue-500/30 transition-all duration-300"
              />
              <span className="hidden sm:block text-lg font-bold tracking-tight text-white font-['Space_Grotesk']">
                AI Workshop <span className="text-[#3b82f6]">LLC</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-7">
              {[
                { to: "/", label: t("nav.home") },
                { to: "/audit", label: t("nav.audit") },
                { to: "/discover", label: "Discover" },
                { to: "/signal-inbox", label: "Signal Inbox", badge: "New" },
                { to: "/my-reports", label: "My Reports" },
              ].map(({ to, label, badge }) => (
                <Link
                  key={to}
                  to={to}
                  className="relative text-slate-400 hover:text-[#3b82f6] text-sm font-medium transition-colors duration-200 tracking-tight inline-flex items-center gap-1.5"
                >
                  {label}
                  {badge && (
                    <span className="text-[9px] font-bold uppercase tracking-wider bg-purple-500/20 text-purple-300 border border-purple-500/30 px-1.5 py-0.5 rounded-full leading-none">
                      {badge}
                    </span>
                  )}
                </Link>
              ))}

              {/* Language selector */}
              <select
                value={lang}
                onChange={handleLangChange}
                aria-label={t("nav.language")}
                className="bg-slate-900/80 border border-slate-700/60 text-xs text-slate-400 rounded-full px-3 py-1.5 focus:outline-none focus:border-blue-500/50 transition-colors"
              >
                {Object.entries(languageNames).map(([code, info]) => (
                  <option key={code} value={code}>
                    {info.flag} {info.name}
                  </option>
                ))}
              </select>

              <button
                onClick={() => navigate("/audit")}
                className="bg-gradient-to-br from-[#3b82f6] to-blue-500 text-slate-950 font-bold text-sm px-6 py-2.5 rounded-full shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 hover:scale-[1.03] active:scale-[0.97] transition-all duration-200"
              >
                {t("hero.cta")}
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-slate-400 hover:text-white transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-slate-800/60 bg-slate-950/95 backdrop-blur-xl rounded-b-3xl">
            <div className="flex flex-col gap-1 px-6 py-6">
              {[
                { to: "/", label: t("nav.home") },
                { to: "/audit", label: t("nav.audit") },
                { to: "/discover", label: "Discover" },
                { to: "/signal-inbox", label: "Signal Inbox", badge: "New" },
                { to: "/my-reports", label: "My Reports" },
              ].map(({ to, label, badge }) => (
                <Link
                  key={to}
                  to={to}
                  className="py-3 text-slate-300 hover:text-[#3b82f6] font-medium transition-colors text-base inline-flex items-center gap-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {label}
                  {badge && (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-purple-500/20 text-purple-300 border border-purple-500/30 px-1.5 py-0.5 rounded-full leading-none">
                      {badge}
                    </span>
                  )}
                </Link>
              ))}

              <div className="flex items-center justify-between pt-4 pb-2 border-t border-slate-800/60">
                <span className="text-xs text-slate-500">{t("nav.language")}</span>
                <select
                  value={lang}
                  onChange={handleLangChange}
                  className="bg-slate-900 border border-slate-700 text-sm text-white rounded-lg px-2 py-1"
                >
                  {Object.entries(languageNames).map(([code, info]) => (
                    <option key={code} value={code}>
                      {info.flag} {info.name}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={() => { navigate("/audit"); setIsMenuOpen(false); }}
                className="mt-3 w-full bg-gradient-to-br from-[#3b82f6] to-blue-500 text-slate-950 font-bold py-4 rounded-full shadow-lg"
              >
                {t("hero.cta")}
              </button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
